import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { CopyField } from './CopyField';
import { SpikeGate } from './SpikeGate';

function Field({ name, children }: { name: string; children: ReactNode }) {
  return (
    <li>
      <code>{name}</code> → {children}
    </li>
  );
}

export function ShortcutsPage() {
  return (
    <SpikeGate>
      {({ config, lock }) => {
        const origin = window.location.origin;
        const header = `Bearer ${config.captureToken}`;
        return (
          <div className="stack">
            <h1>Build the Shortcuts</h1>
            <p className="version">
              Do this on your iPhone, in the <strong>Shortcuts</strong> app. Each one takes about 5
              to 10 minutes.
            </p>

            <section className="card" aria-labelledby="note-title">
              <h2 id="note-title" className="card-heading">
                Read this first
              </h2>
              <ul className="plain">
                <li>
                  Shortcuts can't be sent to you as a file (iPhone only accepts ones signed by
                  Apple), so you build them by hand with the values below.
                </li>
                <li>
                  Menu names follow Apple's current Shortcuts app as best I know it. If something
                  looks different, tell me what you see.
                </li>
                <li>
                  The first time each Shortcut runs, iPhone asks permission to send data to this
                  address. Choose <strong>Always Allow</strong>.
                </li>
                <li>
                  The <em>capture token</em> below only lets a Shortcut post data. It can't read
                  anything.
                </li>
              </ul>
              <CopyField label="Header value (for all three)" value={header} />
            </section>

            <section className="card" aria-labelledby="r1-title">
              <h2 id="r1-title" className="card-heading">
                1 · Save to Personal OS (share sheet)
              </h2>
              <ol className="steps">
                <li>
                  Shortcuts → <strong>+</strong> → name it <strong>Save to Personal OS</strong>.
                </li>
                <li>
                  Open the shortcut's details (ⓘ) → turn on <strong>Show in Share Sheet</strong>.
                  Keep <strong>URLs</strong>, <strong>Safari web pages</strong> and{' '}
                  <strong>Text</strong>; turn the others off.
                </li>
                <li>
                  Add the action <strong>Get Contents of URL</strong>.
                  <CopyField label="URL" value={`${origin}/api/spike/capture`} />
                  Tap <strong>Show More</strong>, then set:
                  <ul className="plain">
                    <li>
                      <strong>Method</strong>: POST
                    </li>
                    <li>
                      <strong>Headers</strong>: add <code>Authorization</code> with the header value
                      above
                    </li>
                    <li>
                      <strong>Request Body</strong>: JSON, then add these fields (tap the blue
                      variable chip to choose it):
                      <ul className="plain">
                        <Field name="url">
                          <em>Shortcut Input</em>
                        </Field>
                        <Field name="title">
                          <em>Shortcut Input</em>, then tap it and choose <strong>Name</strong>
                        </Field>
                        <Field name="text">
                          <em>Shortcut Input</em>
                        </Field>
                      </ul>
                    </li>
                  </ul>
                </li>
                <li>
                  Optional: add <strong>Show Notification</strong> with the text “Saved”.
                </li>
                <li>
                  <strong>Test:</strong> in Safari or YouTube tap Share →{' '}
                  <strong>Save to Personal OS</strong>. It should appear under <em>Received</em>.
                </li>
              </ol>
            </section>

            <section className="card" aria-labelledby="r2-title">
              <h2 id="r2-title" className="card-heading">
                2 · Personal OS note (Siri)
              </h2>
              <ol className="steps">
                <li>
                  New shortcut named exactly <strong>Personal OS note</strong> (not “Quick note”;
                  that's an Apple Notes feature).
                </li>
                <li>
                  Add <strong>Dictate Text</strong>.
                </li>
                <li>
                  Add <strong>Get Contents of URL</strong>.
                  <CopyField label="URL" value={`${origin}/api/spike/note`} />
                  Method <strong>POST</strong>, header <code>Authorization</code> as above, Request
                  Body <strong>JSON</strong> with one field:
                  <ul className="plain">
                    <Field name="text">
                      <em>Dictated Text</em>
                    </Field>
                  </ul>
                </li>
                <li>
                  <strong>Test:</strong> say “Hey Siri, Personal OS note”, then speak a sentence.
                </li>
              </ol>
            </section>

            <section className="card" aria-labelledby="r3-title">
              <h2 id="r3-title" className="card-heading">
                3 · Wallet test (Apple Pay automation)
              </h2>
              <ol className="steps">
                <li>
                  Shortcuts → <strong>Automation</strong> tab → <strong>+</strong> →{' '}
                  <strong>Transaction</strong>.
                </li>
                <li>Choose the card(s) you pay with, then Next.</li>
                <li>
                  Choose <strong>Run Immediately</strong> (so it doesn't ask each time), then Next →{' '}
                  <strong>New Blank Automation</strong>.
                </li>
                <li>
                  Add <strong>Get Contents of URL</strong>.
                  <CopyField label="URL" value={`${origin}/api/spike/wallet`} />
                  Method <strong>POST</strong>, header <code>Authorization</code> as above, Request
                  Body <strong>JSON</strong> with these fields (tap the variable chip{' '}
                  <em>Shortcut Input</em> and choose the property):
                  <ul className="plain">
                    <Field name="amount">
                      <em>Shortcut Input</em> → <strong>Amount</strong>
                    </Field>
                    <Field name="merchant">
                      <em>Shortcut Input</em> → <strong>Merchant</strong>
                    </Field>
                    <Field name="name">
                      <em>Shortcut Input</em> → <strong>Name</strong>
                    </Field>
                    <Field name="card">
                      <em>Shortcut Input</em> → <strong>Card or Pass</strong>
                    </Field>
                    <Field name="everything">
                      <em>Shortcut Input</em> (the whole thing)
                    </Field>
                  </ul>
                  If a property isn't in the list on your phone, skip it. What arrives is exactly
                  what we're trying to learn.
                </li>
                <li>
                  <strong>Test:</strong> make a small Apple Pay purchase and wait a minute. Pressing
                  the play button by hand sends empty values; that's normal.
                </li>
              </ol>
            </section>

            <p>
              <Link to="/spike">← Back to the test page</Link>
            </p>
            <p>
              <button type="button" onClick={lock}>
                Lock this page
              </button>
            </p>
          </div>
        );
      }}
    </SpikeGate>
  );
}
